import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

import type { Van } from "../../service/TransportVehicle/transportVehicle"
import type { Driver } from "../../service/transport"

export interface Student {
  _id: string
  first_name: string
  last_name: string
  father_name?: string
  class?: string
  roll_number?: string
  grNumber?: string
}

export interface TransportAllocation {
  _id: string
  student: Student
  van: Van
  driver: Driver
  driverImg?: string
  pickupStop: string
  startDate: string
  status: "active" | "inactive"
  branch: string
  feeAmount?: number
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type TransportAllocationResponse = {
  data: TransportAllocation[]
  pagination: PaginationData
}

const TRANSPORT_ALLOCATION_ENDPOINT = {
  ALLOCATIONS: "/transport-allocations",
}

const getAllAllocations = async ({
  page = 1,
  limit = 10,
  status,
  vanId,
  driverId,
  studentId,
  searchText,
  sortBy,
  sortOrder,
  system,
}: {
  page?: number
  limit?: number
  status?: string
  vanId?: string
  driverId?: string
  studentId?: string
  searchText?: string
  sortBy?: string
  sortOrder?: "asc" | "desc"
  system?: string
} = {}): Promise<TransportAllocationResponse> => {
  const response = await apiClient({
    url: appendQueryParams(TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS, {
      page,
      limit,
      status,
      vanId,
      driverId,
      studentId,
      sortBy,
      sortOrder,
      searchText,
      system,
    }),
    method: "GET",
  })

  return response.data
}

const createAllocation = async (payload: {
  studentId: string
  vanId: string
  driverId: string
  pickupStop: string
  sessionId?: string
  classId?: string
  feeAmount?: number
}) => {
  const response = await apiClient({
    url: TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS,
    method: "POST",
    data: payload,
  })
  return response.data
}

const getAllocationByStudentOrDriver = async (params: {
  vanId?: string
  driverId?: string
}) => {
  const response = await apiClient({
    url: appendQueryParams(
      `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/student/driver`,
      params,
    ),
    method: "GET",
  })
  return response.data
}

const updateAllocation = async (
  id: string,
  payload: {
    studentId?: string
    vanId?: string
    driverId?: string
    shiftId?: string
    route?: string
    pickupStop?: string
    status?: string
    sessionId?: string
    classId?: string
    feeAmount?: number
  },
) => {
  const response = await apiClient({
    url: `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/${id}`,
    method: "PUT",
    data: payload,
  })
  return response.data
}

const deleteAllocation = async (id: string) => {
  const response = await apiClient({
    url: `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/${id}`,
    method: "DELETE",
  })
  return response.data
}

// ========== CONTRACTOR PAYOUT TYPES ==========

/** Contractor payout summary */
export interface ContractorPayoutSummary {
  contractorId: string
  contractorName: string
  endDate: string
  commissionPercentage: number
  totalTransportCollected: number
  schoolCommissionAmount: number
  contractorShare: number
  totalDue: number
  amountPaidBySchool: number
  studentCount: number
}

/** Contractor payout record row */
export interface ContractorPayoutRecordRow {
  _id: string
  contractor: {
    _id: string
    name?: string
    contractorId?: string
    commissionPercentage?: number
  }
  contractorName?: string
  endDate: string
  totalTransportCollected: number
  schoolCommissionPercent: number
  contractorShare: number
  totalDue: number
  amountPaidBySchool: number
  studentCount: number
}

// ========== CONTRACTOR PAYOUT FUNCTIONS==========

const getContractorPayoutSummary = async (
  contractorId: string,
  endDate: string,
): Promise<ContractorPayoutSummary> => {
  const response = await apiClient({
    url: appendQueryParams(
      `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/contractor-payout`,
      { contractorId, endDate },
    ),
    method: "GET",
  })
  return response.data
}

const recordContractorPayoutPayment = async (
  contractorId: string,
  endDate: string,
  amountPaid: number,
) => {
  const response = await apiClient({
    url: `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/contractor-payout/record-payment`,
    method: "POST",
    data: { contractorId, endDate, amountPaid },
  })
  return response.data
}

const listContractorPayoutRecords = async (
  params: {
    contractorId?: string
    startDate?: string
    endDate?: string
  } = {},
): Promise<ContractorPayoutRecordRow[]> => {
  const response = await apiClient({
    url: appendQueryParams(
      `${TRANSPORT_ALLOCATION_ENDPOINT.ALLOCATIONS}/contractor-payout/records`,
      params,
    ),
    method: "GET",
  })
  return Array.isArray(response.data) ? response.data : []
}

// ========== BACKWARD COMPATIBILITY (if needed) ==========

/**
 * @deprecated Use getContractorPayoutSummary with endDate instead
 */
const getContractorPayoutByMonth = async (
  contractorId: string,
  month: string,
): Promise<ContractorPayoutSummary> => {
  console.warn(
    "Deprecated: Use getContractorPayoutSummary with endDate instead",
  )

  const [year, monthNum] = month.split("-").map(Number)
  const endDate = new Date(year, monthNum, 0) // Last day of month
  const endDateStr = endDate.toISOString().split("T")[0]

  return getContractorPayoutSummary(contractorId, endDateStr)
}

// Export all functions
export {
  getAllAllocations,
  createAllocation,
  getAllocationByStudentOrDriver,
  updateAllocation,
  deleteAllocation,
  // Updated contractor payout functions
  getContractorPayoutSummary,
  recordContractorPayoutPayment,
  listContractorPayoutRecords,
  // Deprecated (optional)
  getContractorPayoutByMonth,
}
